import compact from 'lodash.compact';
import get from 'lodash.get';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import uniq from 'lodash.uniq';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import uniqWith from 'lodash.uniqwith';
import groupBy from 'lodash.groupby';
import maxBy from 'lodash.maxby';
import { KEYS } from '../../constants/shared/InstructionConstants';
import {
    getPeriodByDate,
    getPreviousPeriod,
    LICENSE,
    PERIOD,
} from '../../constants/shared/CommonConstants';
import { getMeasureTitleByKey } from './measureUtils';
import { sortByPredefinedOrder } from './sortUtils';
import getPmInterp from '../../features/instruction/shared/InstructionPmConverter';
import { GRADE } from '../../constants/shared/GradesConstants';

const { PM } = PERIOD;

export const getActivities = (ids, activitiesData) =>
    compact(ids.map(id => activitiesData.find(item => item.id === id)));

export const getActivitiesIdsBySkillAndGrade = (skill, grade, mapper) =>
    get(mapper, `${skill}.${grade}`, []);

export const getAvailableGradesForSkill = (skill, mapper) => Object.keys(get(mapper, skill, {}));

const CAN = 'Can';
const NEED = 'Need';
const NEED_TO_WORK_ON = `${NEED} to work on`;
const NEED_TO_CONTINUE = `${NEED} to continue`;
const STUDENTS_IN_THIS_GROUP = 'Students in this group have';

const emphasizeTextPart = (text, toEmphasize) => (
    <ReactMarkdown>
        {text.replace(new RegExp(`^${toEmphasize}`), `**${toEmphasize}**`)}
    </ReactMarkdown>
);

export const buildProfileDescription = (description = []) => {
    return description.map(text => {
        if (text.startsWith(CAN)) return emphasizeTextPart(text, CAN);
        if (text.startsWith(NEED_TO_WORK_ON)) return emphasizeTextPart(text, NEED_TO_WORK_ON);
        if (text.startsWith(NEED_TO_CONTINUE)) return emphasizeTextPart(text, NEED_TO_CONTINUE);
        return text;
    });
};

export const buildRecommendationsText = (firstName, description = []) => {
    let canText = get(description, [0], '');
    let needText = get(description, [1], '');

    if (canText.startsWith(STUDENTS_IN_THIS_GROUP)) {
        canText = canText.replace(new RegExp(`^${STUDENTS_IN_THIS_GROUP}`), 'has');
    }
    if (canText.startsWith(CAN)) {
        canText = canText.replace(new RegExp(`^${CAN}`), 'can');
    }
    if (needText.startsWith(NEED)) {
        needText = needText.replace(new RegExp(`^${NEED}`), 'needs');
    }
    return `${firstName} ${canText} ${firstName} ${needText}`;
};

/**
 * Make unique observation id for observations with pattern (vowel)
 */
const buildCompositeObservationKey = (id, pattern) => (pattern ? `${id}-${pattern}` : id);

/**
 * Fill in item parameters
 * Example: "{student_first_name} successfully decoded {num_correct_items}"
 * @param {string} item The string which contains parameters to replace
 * @param {Array} parametersToReplace The array of parameters to replace
 * @param {Object} itemParameters The object which contains item parameters
 * @param {string} firstName (optional) The student first name
 * @returns {string} The string with filled in parameters
 */
export const fillInItemParameters = (item, parametersToReplace, itemParameters, firstName) => {
    let updatedItem = item;
    parametersToReplace.forEach(parameter => {
        updatedItem = updatedItem
            .replace(
                new RegExp(`{${parameter}}`, 'g'),
                parameter === 'student_first_name' ? firstName : get(itemParameters, parameter, ''),
            )
            .replace('<BR><BR>', '');
    });
    return updatedItem;
};

/**
 * Merge observations with content and sort by d8 measure order
 */
export const buildObservations = (content, observations, firstName, measureList) => {
    const observationsWithContent = compact(
        observations.map(({ id, parameters }) => {
            const contentItem = content.find(item => item.id === id) || {};
            const { name, measure, parameters: contentItemParameters, description } = contentItem;
            return isEmpty(contentItem)
                ? null
                : {
                      id: buildCompositeObservationKey(id, get(parameters, 'pattern')),
                      name: fillInItemParameters(name, contentItemParameters, parameters),
                      measure,
                      measureTitle: getMeasureTitleByKey(measureList, measure),
                      description: fillInItemParameters(
                          description,
                          contentItemParameters,
                          parameters,
                          firstName,
                      ),
                  };
        }),
    );
    return sortByPredefinedOrder(
        observationsWithContent,
        measureList.map(measure => measure.key),
        KEYS.MEASURE,
    );
};

/**
 * Return list of activities by observation id, mapper and grade (if applicable)
 * If there are no activities found, try to find activities ignoring the second part of observation composite key (pattern)
 * If there are no grade-specific activities returns a whole list corresponding with observation id
 */
const getActivitiesByObservationAndGrade = (id, observationActivitiesMapper, grade) => {
    let activities = observationActivitiesMapper[id];

    if (isNil(activities)) {
        const idWithoutPattern = id.substring(0, id.lastIndexOf('-'));
        activities = get(observationActivitiesMapper, idWithoutPattern, []);
    }
    return get(activities, grade, activities);
};

/**
 * Merge static (based on instruction group) and dynamic (based on selected observations and selected grade (if applicable)) activities
 * If there are no selected observations, activities for all the observations are shown
 * Returns only unique and existing activities sorted by id
 *
 * Example of @param observationActivitiesMapper based on observation only and grade-specific combinations:
   {
    "a03f5edd-bde0-4ed9-8d5c-4e52bdd5d857": [1, 2]
   },
   {
    "c5be882b-1621-4a20-a1f7-c66ce62efcaf": {
      "01": [3,4],
      "02": [5,6]
    }
   }
 */
export const buildActivitiesIds = (
    staticActivitiesIds,
    allObservations,
    selectedObservationsIds,
    observationActivitiesMapper,
    selectedGrade,
) =>
    uniq(
        staticActivitiesIds.concat(
            ...(isEmpty(selectedObservationsIds)
                ? allObservations.map(observation => observation.id)
                : selectedObservationsIds
            ).map(id =>
                getActivitiesByObservationAndGrade(id, observationActivitiesMapper, selectedGrade),
            ),
        ),
    ).sort((firstActivityId, secondActivityId) => firstActivityId - secondActivityId);

/**
 * If assessing in current period is already started (at least one student in the class
 * was assessed by any of provided measures, PM results are also counted)
 * the current benchmark period will be returned.
 * Otherwise, return the previous benchmark period for avoiding an empty page
 */
export const getInstructionPeriod = (
    studentsResults,
    currentYear,
    currentPeriod,
    measures,
    windowDates,
    toyPeriods,
    selectedInstructionGrade = GRADE.KINDERGARTEN,
    licensingInfo = [],
) => {
    const isAssessingStarted = Object.keys(studentsResults).some(id =>
        studentsResults[id].statuses.find(({ period, date, schoolYear, measure, isComplete }) => {
            const statusPeriod = period === PM ? getPeriodByDate(date, windowDates) : period;
            return (
                currentYear === schoolYear &&
                currentPeriod === statusPeriod &&
                measures.some(measureKey => measure === measureKey && isComplete)
            );
        }),
    );

    // Apply the following logic when the Summer period is selected:
    // - If LDOE && Grade K-2 → Use MOY
    // - If LDOE && Grade 3 → Use EOY
    if (
        licensingInfo.includes(LICENSE.LOUISIANA) &&
        isAssessingStarted &&
        currentPeriod === PERIOD.SUMMER
    ) {
        return [GRADE.KINDERGARTEN, GRADE.FIRST_GRADE, GRADE.SECOND_GRADE].includes(
            selectedInstructionGrade,
        )
            ? PERIOD.MOY
            : PERIOD.EOY;
    }
    return isAssessingStarted ? currentPeriod : getPreviousPeriod(currentPeriod, toyPeriods);
};

export const extractObservationsFromStatus = status => get(status, KEYS.OBSERVATIONS, []);

/**
 * Filter observations by their affiliation to measure
 * @param status
 * @param observations
 * @returns list of observations from status(only its own)
 */
export const getMatchedWithContentObservations = (status, observations) => {
    return extractObservationsFromStatus(status).filter(observationFromStatus =>
        observations.some(
            observation =>
                observation.id === observationFromStatus.id &&
                observation.measure === status.measure,
        ),
    );
};

export const compareObservations = (observation1, observation2) => {
    const pattern1 = get(observation1, 'parameters.pattern');
    const pattern2 = get(observation2, 'parameters.pattern');
    if (pattern1 && pattern2) return observation1.id === observation2.id && pattern1 === pattern2;
    return observation1.id === observation2.id;
};

export const generateObservations = observationStatuses =>
    uniqWith(observationStatuses, (observation1, observation2) =>
        compareObservations(observation1, observation2),
    );

/**
 * Populate PM results with interpretations based on score, grade, instruction period and measure
 */
const populateStatusesWithPmInterps = (statuses, instructionPeriod, pmThresholdsMapper) =>
    statuses.map(status => {
        const { score, grade, period, measure } = status;
        return period === PERIOD.PM
            ? {
                  ...status,
                  interp: getPmInterp(score, grade, instructionPeriod, measure, pmThresholdsMapper),
              }
            : status;
    });

/**
 * Returns an array of the statuses with the highest score for each measure for instruction period.
 * If there are statuses with the same scores, mergedStatuses order matters, e.g. the next:
 * a) BM status
 * b) latest PM status
 */
export const buildBestStatuses = (mergedStatuses, period, pmThresholdsMapper) =>
    compact(
        Object.values(groupBy(mergedStatuses, result => result.measure)).map(statusesByMeasure =>
            maxBy(
                populateStatusesWithPmInterps(statusesByMeasure, period, pmThresholdsMapper),
                status => status.score,
            ),
        ),
    );

export const getActivityIdFromUrl = activityUrl => {
    const match = activityUrl.match(/activity_pdf%2F(\d*)/);

    return match ? match[1] : '';
};
